import React, { FC, useEffect, useRef, useState } from 'react'
import { Meeting } from '../../../interfaces'
import { KTSVG } from '../../../_metronic/helpers'
import { useFormik } from 'formik'
import { useIntl } from 'react-intl'
import { CustomField } from '../../../interfaces/Settings'
import { useSettingsStore } from '../../../stores/SettingsStore'
import * as Yup from 'yup'
import { customThousandSeparatorDot, getDataById, handleCaps, isValidUrl, unformatNumberInput } from '../../../functions/general'
import useHandleCloseModal from '../../../hooks/HandleCloseModal'
import moment from "moment";
import Select from 'react-select'
import { nanoid } from '@reduxjs/toolkit'
import { SelectOption } from '../../../interfaces/SelectOption'
import clsx from 'clsx'
import InputAttachment from '../../Input/InputAttachment'
import useUser from '../../../hooks/User'
import { useCustomeStore } from '../../../stores/CustomeStore'
import { Label } from '../../../interfaces/Label'
import RenderCustomFieldGeneralForm from '../../RenderComponent/RenderCustomFieldGeneralForm'
import Swal from 'sweetalert2'
import useAccessToken from '../../../hooks/AccessToken'
import { useMeetingStore } from '../../../stores/MeetingsStore'
import { useUserStore } from '../../../stores/UserStore'
import { usePipelineStore } from '../../../stores/PipelineStore'
import { useMerchantStore } from '../../../stores/MerchantStore'
import { insertAttachment, insertMultiAttachment } from '../../../api/ResourceAPI'
import { updateMeeting, updateScheduleVisitMeeting } from '../../../api/MeetingsCRUD'
import { useDataTableStore } from '../../../stores/DataTableStore'
import { useParams } from 'react-router-dom'
import { useProductStore } from '../../../stores/ProductStore'

interface UpdateVisitScheduleMeetingProps {
    meeting: Meeting
    customFields: CustomField
    labels: Label[]
}

const API_URL = process.env.REACT_APP_API_URL

const UpdateVisitScheduleMeeting: FC<UpdateVisitScheduleMeetingProps> = ({ meeting, customFields, labels }) => {
    const intl = useIntl()
    const { settings } = useSettingsStore()
    const modalRef = useRef<HTMLDivElement>(null)
    const closeModalRef = useRef<HTMLButtonElement>(null)
    const [statusData, setStatusData] = useState<SelectOption[]>([
        {
            value: "CANCELLED",
            label: intl.formatMessage({ id: 'MEETING.STATUS.CANCELLED' })
        },
    ]);
    const user = useUser()
    const { companyId } = useCustomeStore()
    const [labelsOption, setLabelOption] = useState<SelectOption[]>([])
    const { simpleMerchants } = useMerchantStore()
    const { simplePipelines } = usePipelineStore()
    const { selectMultiMeeting, simpleMeetings } = useMeetingStore()
    const { simpleProducts } = useProductStore()
    const { users } = useUserStore()
    const token = useAccessToken()
    const { setTablesData, tablesData } = useDataTableStore()
    const { templateMeetingId } = useParams()
    const [toGoogleCalendar, setToGoogleCalendar] = useState(user?.data?.sync_google_calendar ? true : false);

    const formSchema = Object.keys(customFields).reduce((obj: any, item: string) => {
        const field = customFields[item];
        const isRequiredMessage = intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: field.name });
        const isValidUrlMessage = intl.formatMessage({ id: "FORM.VALIDATION.MUST_LINK" });

        if (field.type === 'multiple' || field.type === 'multipleOption' || field.type === 'images' || field.type === 'files' || field.type === 'merchant' || field.type === 'user' || field.type === 'meeting' || field.type === 'product') {
            obj[item] = field.required && settings.update_meeting
                ? Yup.array().min(1, isRequiredMessage).required(isRequiredMessage).nullable()
                : Yup.array().notRequired().nullable();
        } else if (field.type === 'date') {
            obj[item] = field.required && settings.update_meeting
                ? Yup.string().required(isRequiredMessage).nullable()
                : Yup.string().notRequired().nullable();
        } else if (field.type === 'link') {
            obj[item] = field.required && settings.update_meeting
                ? Yup.string().nullable().required(isRequiredMessage).test('is-required', isValidUrlMessage, function (value) {
                    return !value || isValidUrl(value);
                })
                : Yup.string().nullable().test('is-required', isValidUrlMessage, function (value) {
                    return !value || isValidUrl(value);
                });
        } else if (field.type === 'serial') {
            obj[item] = Yup.array().notRequired().nullable();
        } else if (field.type === 'dateTimeRange') {
            if (field.required && settings.update_meeting) {
                obj[item] = Yup.array().min(1, isRequiredMessage).required(isRequiredMessage).nullable();
            } else {
                obj[item] = Yup.array().notRequired().nullable();
            }
        } else if (field.type === 'email') {
            obj[item] = field.required
                ? Yup.string().email(intl.formatMessage({ id: "FORM.VALIDATION.EMAIL" })).required(isRequiredMessage).nullable()
                : Yup.string().email(intl.formatMessage({ id: "FORM.VALIDATION.EMAIL" })).notRequired().nullable();
        } else {
            obj[item] = field.required && settings.update_meeting
                ? Yup.string().required(isRequiredMessage).nullable()
                : Yup.string().notRequired().nullable();
        }

        return obj;
    }, {});

    const validationSchema = Yup.object().shape({
        attachment: Yup.mixed().test({
            name: 'attachment',
            exclusive: true,
            message: intl.formatMessage(
                { id: "FORM.VALIDATION.FIELD_REQUIRED" },
                { title: intl.formatMessage({ id: "FORM.LABEL.PHOTO" }, { title: settings.meeting_title }) }
            ),
            test: function (value) {
                const isMeetingVisitWithDisableCapture = meeting.type === 'VISIT' && settings.meeting_visit_disable_capture_camera;
                const isRequired =
                    this.parent &&
                    (this.parent.status === "COMPLETED" || this.parent.status === "ONGOING") &&
                    !isMeetingVisitWithDisableCapture;

                if (this.parent && this.parent.formik && this.parent.formik.setTouched) {
                    this.parent.formik.setTouched({
                        ...this.parent.formik.touched,
                        attachment: isRequired,
                    });
                }

                return isRequired && !settings.meeting_done_disable_capture_camera ? Boolean(value) : true;
            }
        }).nullable(),
        labels: Yup.array().notRequired(),
        ...formSchema
    });

    const formik = useFormik({
        initialValues: {
            status: meeting.meeting_status === intl.formatMessage({ id: 'MEETING.STATUS.CANCELLED' }) ? "CANCELLED" : "",
            attachment: (meeting.attachments !== null && meeting.attachments.length > 0) ? meeting.attachments[0].url : "",
            note: meeting.note ? meeting.note : "",
            labels: meeting.labels.map((label) => { return label.id }),
            ...Object.fromEntries(Object.keys(customFields).map((key) => [key,
                meeting.custom_fields && meeting.custom_fields[key] ?
                    customFields[key].type === 'date' ?
                        moment(meeting.custom_fields[key], ['YYYY-MM-DD', 'YYYY/MM/DD'], true).isValid() ?
                            meeting.custom_fields[key]
                            : null
                        : customFields[key].type === 'number' ?
                            customThousandSeparatorDot(meeting.custom_fields[key] as unknown as number)
                            : meeting.custom_fields[key]
                    : null]
            )),

        },
        validationSchema: validationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            setSubmitting(true)

            const customFieldsValues: Record<string, string | string[] | number | null> = Object.keys(customFields).reduce(
                (obj, item) => ({
                    ...obj,
                    [item]: (values as Record<string, string | string[] | number | null>)[item],
                }),
                {}
            );

            const convertCustomFields: Record<string, any> = Object.keys(customFields).reduce(
                (obj, item) => {
                    if (!obj.info_custom_fields) {
                        obj.info_custom_fields = {};
                    }

                    if (customFields[item].type === 'merchant') {
                        const selectedMerchants = getDataById(values[item as keyof typeof values] as any, simpleMerchants);
                        obj[`custom_fields.${item}`] = selectedMerchants?.map((merchant: any) => merchant.name).join(", ");
                        obj.info_custom_fields[item] = selectedMerchants?.map((merchant: any) => ({
                            merchant_template_id: merchant.merchantTemplateId,
                            merchant_id: merchant.id,
                            merchant_name: merchant.name
                        })) || [];
                    }
                    else if (customFields[item].type === 'user') {
                        const selectedUsers = getDataById(values[item as keyof typeof values] as any, users);
                        obj[`custom_fields.${item}`] = selectedUsers?.map((user: any) => user.name).join(", ");
                        obj.info_custom_fields[item] = selectedUsers?.map((user: any) => ({
                            id: user.id,
                            name: user.name
                        })) || [];
                    }
                    else if (customFields[item].type === 'pipeline') {
                        const selectedPipeline = getDataById(values[item as keyof typeof values] as string, simplePipelines);
                        obj[`custom_fields.${item}`] = selectedPipeline ?
                            `${selectedPipeline.name} / ${selectedPipeline.nominal} / ${moment(selectedPipeline.dateCreated).format('DD MMM YYYY')}`
                            : '-';
                        obj.info_custom_fields[item] = selectedPipeline ? [{
                            id: selectedPipeline.id,
                            name: selectedPipeline.name,
                            nominal: selectedPipeline.nominal,
                            dateCreated: selectedPipeline.dateCreated
                        }] : [];
                    }
                    else if (customFields[item].type === 'meeting') {
                        const selectedMeeting: any[] = getDataById(values[item as keyof typeof values] as string, simpleMeetings);
                        obj[`custom_fields.${item}`] = selectedMeeting && selectedMeeting.length > 0 ?
                            selectedMeeting.map((meeting: any) => `${meeting.name} / ${moment(meeting.dateMeet).format('DD MMM YYYY')} / ${meeting.meetingStatus}`).join(', ')
                            : '-';
                        obj.info_custom_fields[item] = selectedMeeting && selectedMeeting.length > 0 ? selectedMeeting.map((meeting: any) => ({
                            meeting_template_id: meeting.meetingTemplateId,
                            id: meeting.id,
                            merchant_name: meeting.name,
                            date_meet: meeting.dateMeet,
                            meeting_status: meeting.meetingStatus
                        })) : [];
                    } else if (customFields[item].type === 'product') {
                        const selectedProducts = getDataById(values[item as keyof typeof values] as any, simpleProducts);
                        obj[`custom_fields.${item}`] = selectedProducts?.map((product: any) => product.name).join(", ");
                        obj.info_custom_fields[item] = selectedProducts?.map((product: any) => ({
                            product_id: product.id,
                            product_name: product.name,
                            product_prices: product.product_prices,
                        })) || [];
                    }
                    else {
                        obj[`custom_fields.${item}`] = values[item as keyof typeof values] || '';
                        obj.info_custom_fields[item] = [];
                    }

                    return obj;
                },
                {} as Record<string, any>
            );

            // Use Promise.all to await all async operations
            let countResourceError = 0
            await Promise.all(
                Object.keys(customFields).map(async (key) => {
                    if (customFields[key].type === 'image' || customFields[key].type === 'file' || customFields[key].type === 'video') {
                        try {
                            if (values[key as keyof typeof values] && typeof values[key as keyof typeof values] === 'object') {
                                const resourceResponse = await insertAttachment(values[key as keyof typeof values] as string | File, 'MEETING', token);
                                if (resourceResponse.data.success) customFieldsValues[key] = resourceResponse.data.data.length > 0 ? resourceResponse.data.data[0] : null;
                                else countResourceError++
                            }
                        } catch (error) {
                            countResourceError++
                        }
                    } else if (customFields[key].type === 'images' || customFields[key].type === 'files' || customFields[key].type === 'videos') {
                        try {
                            if (values[key as keyof typeof values]) {
                                let dataMultiAttachment = (values[key as keyof typeof values] as any).filter((item: any) => typeof item !== 'string')
                                let dataUrlAttachment = (values[key as keyof typeof values] as any).filter((item: any) => typeof item === 'string')

                                const resourceResponse = await insertMultiAttachment(dataMultiAttachment, 'MEETING', token);
                                if (resourceResponse.data.success) (customFieldsValues[key] as any) = resourceResponse.data.data.length > 0 ? [...dataUrlAttachment, ...resourceResponse.data.data] : dataUrlAttachment.length > 0 ? dataUrlAttachment : null;
                                else countResourceError++
                            }
                        } catch (error) {
                            countResourceError++
                        }
                    } else if (customFields[key].type === 'multiple' || customFields[key].type === 'multipleOption') {
                        var multipleValues = values[key as keyof typeof values]?.length! > 0 ? values[key as keyof typeof values] : null;
                        customFieldsValues[key] = multipleValues as string
                    } else if (customFields[key].type === 'dateTimeRange') {
                        if (values[key as keyof typeof values]) {
                            const [startDate, endDate] = values[key as keyof typeof values] as any;
                            customFieldsValues[key] = [
                                startDate ? (new Date(startDate).toISOString() as any) : null,
                                endDate ? (new Date(endDate).toISOString() as any) : null
                            ];
                        } else {
                            customFieldsValues[key] = null;
                        }
                    } else if (customFields[key].type === 'number') {
                        const stringValue = values[key as keyof typeof values] as string;
                        customFieldsValues[key] = stringValue ? unformatNumberInput(stringValue) : null;
                    }
                })
            );

            if (countResourceError > 0) {
                Swal.fire({
                    icon: "error",
                    text: `${intl.formatMessage({ id: "FORM.VALIDATION.UPDATE_FAILED" })}.`,
                    heightAuto: false,
                    confirmButtonText: 'Ok'
                })
                setSubmitting(false);
            } else {
                try {
                    const meetingLabels = values.labels.length > 0 && Array.isArray(values.labels) ? values.labels.map((label) => { return { id: label } }) : []
                    let response: any = null
                    if (meeting.meeting_status === intl.formatMessage({ id: 'MEETING.STATUS.PENDING' })) {
                        const newValues = {
                            idMeeting: meeting.id,
                            note: values.note,
                            meetingLabels,
                            token: token,
                            customFields: customFieldsValues
                        }
                        response = await updateScheduleVisitMeeting(newValues)
                    } else {
                        response = await updateMeeting({
                            id: meeting.id,
                            meetingTemplateId: templateMeetingId || '',
                            meetingLabels,
                            start: {
                                date: meeting.allDay ? moment(`${meeting.date_meet}`).format("YYYY-MM-DD") : null,
                                dateTime: !meeting.allDay ? moment(`${meeting.date_meet}`).toISOString(true) : null
                            },
                            end: {
                                date: meeting.allDay ? moment(`${meeting.date_meet_end}`).add(meeting.allDay ? 1 : 0, 'days').format("YYYY-MM-DD") : null,
                                dateTime: !meeting.allDay ? moment(`${meeting.date_meet_end}`).add(meeting.allDay ? 1 : 0, 'days').toISOString(true) : null,
                            },
                            ownerId: meeting.owner_id,
                            attachments: meeting.attachments,
                            meetingStatus: values.status,
                            reminderMinutes: meeting.reminderMinutes,
                            userAttendees: meeting.attendees,
                            note: values.note,
                            token: token,
                            notification: false,
                            latitude: meeting.latitude,
                            longitude: meeting.longitude,
                            location: meeting.location,
                            customFields: customFieldsValues,
                            toGoogleCalendar: toGoogleCalendar
                        })
                    }

                    if (response?.data.success) {
                        Swal.fire({
                            icon: 'success',
                            heightAuto: false,
                            title: `${intl.formatMessage({ id: "FORM.VALIDATION.UPDATE_SUCCESS" })}.`,
                            timer: 2000,
                            timerProgressBar: true
                        })

                        setTablesData('meetings', (tablesData.meetings.map(item => {
                            if (item.id === meeting.id) {
                                return {
                                    ...item,
                                    ...convertCustomFields['custom_fields'],
                                    info_custom_fields: convertCustomFields.info_custom_fields,
                                    custom_fields: customFieldsValues,
                                    meeting_status: values.status === "CANCELLED" ? intl.formatMessage({ id: 'MEETING.STATUS.CANCELLED' }) : meeting.meeting_status,
                                    attachments: [],
                                    note: response.data.data.note,
                                    date_meet: moment(response.data.data.date_meet).format("YYYY-MM-DD HH:mm:ss"),
                                    date_meet_end: response.data.data.allDay ? moment(response.data.data.date_meet_end).subtract(1, 'days').local().format("YYYY-MM-DD HH:mm:ss") : moment(response.data.data.date_meet_end).format("YYYY-MM-DD HH:mm:ss"),
                                    logs: response.data.data.logs,
                                    labels: response.data.data.labels,
                                }
                            } else {
                                return item
                            }
                        })));
                        closeModalRef.current?.click();
                    }
                } catch (error: any) {
                    Swal.fire({
                        icon: 'error',
                        title: error.response.data.message,
                        confirmButtonText: 'Ok',
                        heightAuto: false,
                    })
                } finally {
                    setSubmitting(false)
                }
            }
        }
    })

    useEffect(() => {
        const newLabelsOption = labels.map((label: Label) => { return { value: label.id, label: label.name } })
        setLabelOption(newLabelsOption)
    }, [labels])

    const { handleCloseModal } = useHandleCloseModal(modalRef, closeModalRef, formik);

    const getIsDisabledStatus = () => {
        if (meeting.meeting_status === intl.formatMessage({ id: 'MEETING.STATUS.SCHEDULED' })) {
            return false
        } else if (meeting.type === 'SCHEDULED_VISIT' && meeting.meeting_status === intl.formatMessage({ id: 'MEETING.STATUS.APPROVED' })) {
            return false
        } else {
            return true
        }
    }

    return (
        <div className="modal fade" tabIndex={-1} id={`update-visit-schedule-meeting-modal-${meeting.id}`} ref={modalRef}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title" style={{ overflowWrap: "anywhere" }}>{intl.formatMessage({ id: 'FORM.ACTION.UPDATE' })} Visit Schedule Meeting {meeting.merchant_name} <br /> {intl.formatMessage({ id: 'FORM.LABEL.DATE' }, { title: "" })} {moment(meeting.date_meet).format('DD-MM-YYYY')}</h5>
                        <div
                            className="btn btn-icon btn-sm btn-active-light-primary ms-2"
                            onClick={handleCloseModal}
                        >
                            <KTSVG
                                path="/media/icons/duotune/arrows/arr061.svg"
                                className="svg-icon svg-icon-2x"
                            />
                        </div>
                    </div>
                    <form onSubmit={formik.handleSubmit} noValidate>
                        <div className="modal-body">
                            {
                                meeting.meeting_status !== intl.formatMessage({ id: 'MEETING.STATUS.ONGOING' }) && meeting.meeting_status === intl.formatMessage({ id: 'MEETING.STATUS.APPROVED' }) &&
                                <>
                                    {/* Status */}
                                    <label className='form-label fs-6 fw-bolder text-dark'>{intl.formatMessage({ id: 'FORM.LABEL.STATUS' })}</label>
                                    <div className="input-group mb-3">
                                        <Select
                                            key={nanoid()}
                                            options={statusData}
                                            placeholder={`${intl.formatMessage({ id: "FORM.ACTION.CHOOSE" })} ${intl.formatMessage({ id: "FORM.LABEL.STATUS" })}`}
                                            value={statusData.find(option => option.value === formik.values.status)}
                                            isDisabled={getIsDisabledStatus()}
                                            onChange={
                                                option => formik.handleChange({
                                                    target: {
                                                        name: "status",
                                                        value: option?.value
                                                    }
                                                })
                                            }
                                            onBlur={
                                                () => formik.values.status === "" && formik.setFieldTouched("status", true)
                                            }
                                            isClearable={true}
                                            className="w-100"
                                        />
                                    </div>
                                </>
                            }

                            {/* Attachments */}
                            {
                                meeting.meeting_status === intl.formatMessage({ id: 'MEETING.STATUS.SCHEDULED' }) && !settings.meeting_done_disable_capture_camera &&
                                <>
                                    <label className={`form-label fs-6 fw-bolder text-dark ${(formik.values.status === "COMPLETED" || formik.values.status === "ONGOING") ? 'required' : ''}`}>{intl.formatMessage({ id: 'FORM.LABEL.PHOTO' }, { title: settings.meeting_title })}</label>
                                    <InputAttachment keyString={"attachment"} formik={formik} isMulti={false} type="update-status-meeting" accept="image/png, image/jpeg, image/jpg" />
                                    {formik.touched.attachment && formik.errors.attachment && (
                                        <div className='fv-plugins-message-container text-danger'>
                                            <span role='alert' className="text-danger">{formik.errors.attachment}</span>
                                        </div>
                                    )}
                                </>
                            }

                            {/* Note */}
                            <label className='form-label fs-6 fw-bolder text-dark'>{intl.formatMessage({ id: 'FORM.LABEL.NOTE' })}</label>
                            <div className="input-group mb-3">
                                <textarea
                                    placeholder={intl.formatMessage({ id: 'FORM.LABEL.NOTE' })}
                                    id="floatingTextarea"
                                    {...formik.getFieldProps('note')}
                                    className={clsx(
                                        'form-control form-control-lg',
                                        { 'is-invalid': formik.touched.note && formik.errors.note },
                                    )}
                                    style={{ zIndex: 0 }}
                                    name="note"
                                    onInput={(e) => handleCaps(e)}
                                >
                                </textarea>
                            </div>
                            {formik.touched.note && formik.errors.note && (
                                <div className='fv-plugins-message-container text-danger'>
                                    <span role='alert' className="text-danger">{formik.errors.note}</span>
                                </div>
                            )}
                            {/* label */}
                            {
                                user.data.company_id !== companyId.esb && selectMultiMeeting.isFeatureLabelEnabled &&
                                <>
                                    <label className='form-label fs-6 fw-bolder text-dark '>{intl.formatMessage({ id: 'FORM.LABEL.LABELS' })}</label>
                                    <div className="input-group mb-3">
                                        <Select
                                            closeMenuOnSelect={false}
                                            openMenuOnClick
                                            isMulti
                                            options={labelsOption}
                                            placeholder={`${intl.formatMessage({ id: 'FORM.ACTION.CHOOSE' })} ${intl.formatMessage({ id: 'FORM.LABEL.LABELS' })}`}
                                            value={labelsOption.filter((option: any) =>
                                                formik.values.labels.includes(option.value)
                                            )
                                            }
                                            onChange={(selectedOptions) => {
                                                const selectedValues = selectedOptions
                                                    ? selectedOptions.map((option: any) => option.value)
                                                    : []
                                                formik.setFieldValue('labels', selectedValues)
                                            }}
                                            className='w-100'
                                            onBlur={() =>
                                                formik.values.labels.length == 0 &&
                                                formik.setFieldTouched('labels', true)
                                            }
                                        />
                                    </div>
                                    {formik.touched.labels && formik.errors.labels && (
                                        <div className='fv-plugins-message-container text-danger'>
                                            <span role='alert' className="text-danger">{formik.errors.labels}</span>
                                        </div>
                                    )}
                                </>
                            }
                            {/* Google Calendar Checkbox */}
                            {
                                (user?.data?.sync_google_calendar) && (
                                    <div className="form-check mb-3">
                                        <label className="form-check-label fw-bold" htmlFor="toGoogleCalendar">
                                            {intl.formatMessage({ id: 'FORM.LABEL.TO_GOOGLE_CALENDAR' }) || 'Add to Google Calendar'}
                                        </label>
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="toGoogleCalendar"
                                            checked={toGoogleCalendar}
                                            onChange={() => setToGoogleCalendar(!toGoogleCalendar)}
                                        />
                                    </div>
                                )
                            }
                            {
                                (settings.update_meeting) &&
                                <RenderCustomFieldGeneralForm
                                    customFieldGeneral={customFields}
                                    formik={formik}
                                    errors={formik.errors}
                                    containerRef={document.querySelector(`#update-visit-schedule-meeting-modal-${meeting.id}`) as HTMLElement}
                                    typeAction="update"
                                />
                            }
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-light d-none"
                                data-bs-dismiss="modal"
                                ref={closeModalRef}
                            >
                                {intl.formatMessage({ id: "FORM.ACTION.CANCEL" })}
                            </button>
                            <button
                                type="button"
                                className="btn btn-light"
                                onClick={handleCloseModal}
                            >
                                {intl.formatMessage({ id: "FORM.ACTION.CANCEL" })}
                            </button>
                            <button type="submit" className={`btn btn-primary me-10 ${formik.isSubmitting ? "disabled" : ""}`} data-kt-indicator={formik.isSubmitting ? 'on' : 'off'}>
                                <span className="indicator-label">
                                    {intl.formatMessage({ id: 'FORM.ACTION.UPDATE' })}
                                </span>
                                <span className="indicator-progress">
                                    Loading... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                </span>
                            </button>
                        </div>
                    </form>
                </div>
            </div >
        </div >
    )
}

export default UpdateVisitScheduleMeeting